import { type FC, useState, useEffect } from "react";
import { TextInput, Select } from "flowbite-react";
import { Dialog, Button } from "@components";
import { useTranslation } from "react-i18next";
import type { Company, Invoice } from "../../models";
import { IInvoiceItem } from "../../types/Invoice";
import { HiOutlineTrash, HiPlus } from "react-icons/hi";

export interface EditInvoiceProp {
  show: boolean;
  isLoading: boolean;
  invoice?: Invoice;
  companies: Company[];
  toggleModal: () => void;
  confirmButton: (items: IInvoiceItem[], companyId: string) => void;
}

interface DialogContentProps {
  isLoading: boolean;
  invoice?: Invoice;
  companies: Company[];
  toggleModal: () => void;
  confirmButton: (items: IInvoiceItem[], companyId: string) => void;
}

export const EditInvoice: FC<EditInvoiceProp> = (props) => {
  const { t } = useTranslation(["invoice"]);
  return (
    <Dialog
      size="xl"
      denseHeader
      show={props.show}
      dismissible
      title={t("actions.edit_invoice_dialog_title", "Edit Invoice")}
      toggleModal={props.toggleModal}
      hideButtons={true}
      content={
        <DialogContent
          isLoading={props.isLoading}
          invoice={props.invoice}
          companies={props.companies}
          toggleModal={props.toggleModal}
          confirmButton={props.confirmButton}
        />
      }
    />
  );
};

const DialogContent: FC<DialogContentProps> = (props) => {
  const { t } = useTranslation(["common", "invoice"]);
  const [items, setItems] = useState<IInvoiceItem[]>([]);
  const [companyId, setCompanyId] = useState("");
  const sourceOrders = props.invoice?.sourceOrders || [];
  const linkedIds = props.invoice?.relatedOrderIds || [];
  const invalidSources =
    linkedIds.length > 0 &&
    (items.some((item) => !linkedIds.includes(item.sourceOrderId || "")) ||
      linkedIds.some((id) => !items.some((item) => item.sourceOrderId === id)));

  // Add custom styling to remove input spinners
  useEffect(() => {
    // Add a style tag to the document head
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      .no-spinner::-webkit-inner-spin-button, 
      .no-spinner::-webkit-outer-spin-button { 
        -webkit-appearance: none !important;
        margin: 0 !important;
      }
      .no-spinner {
        -moz-appearance: textfield !important;
        appearance: textfield !important;
      }
      input[type=number].no-spinner {
        -webkit-appearance: none !important;
        margin: 0 !important;
      }
    `;
    document.head.appendChild(styleEl);

    // Clean up function to remove the style tag when component unmounts
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  useEffect(() => {
    if (props.invoice && props.invoice.items) {
      // Make a deep copy of the items to avoid mutating props directly
      setItems(JSON.parse(JSON.stringify(props.invoice.items)));
      setCompanyId(props.invoice.companyId || props.invoice.invoicer?.id || "");
    }
  }, [props.invoice]);

  const selectedCompanyIsDeleted =
    Boolean(companyId) &&
    !props.companies.some((company) => company.id === companyId);

  const handleAddNewItem = () => {
    const newItem: IInvoiceItem = {
      description: "",
      unit: "stk", // Norwegian: "stykker" instead of "pcs"
      quantity: 1,
      price: 0,
      vat: 25, // Default VAT in Norway
      discountInPercent: 0,
      itemTitle: "",
    };
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = (
    index: number,
    field: keyof IInvoiceItem,
    value: any,
  ) => {
    const newItems = [...items];

    // Allow empty string (to clear the field) or convert to number
    if (
      field === "quantity" ||
      field === "price" ||
      field === "vat" ||
      field === "discountInPercent"
    ) {
      if (value === "" || value === null) {
        value = ""; // Store as empty string temporarily
      } else {
        value = parseFloat(value) || 0;
      }
    }

    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    setItems(newItems);
  };

  const unitOptions = ["stk", "km", "kg", "m3"]; // Norwegian: "stk" instead of "pcs"
  const vatOptions = [
    { label: "25%", value: 25 },
    { label: "15%", value: 15 },
    { label: "12%", value: 12 },
    { label: "0%", value: 0 },
  ];

  return (
    <div className="flex flex-col items-start justify-between gap-2">
      <div className="w-full max-w-md mt-4">
        <label
          htmlFor="invoice-company"
          className="block mb-2 text-[14px] font-medium text-secondary"
        >
          {t("invoice:actions.issuing_company")}
        </label>
        <Select
          id="invoice-company"
          disabled={!!props.invoice?.relatedOrderIds?.length}
          value={companyId}
          onChange={(event) => setCompanyId(event.target.value)}
          sizing="sm"
          required
        >
          {!companyId && (
            <option value="" disabled>
              {t("invoice:actions.select_issuing_company")}
            </option>
          )}
          {selectedCompanyIsDeleted && (
            <option value={companyId} disabled>
              {`${props.invoice?.invoicer?.name || companyId} (${t(
                "invoice:actions.deleted_company",
              )})`}
            </option>
          )}
          {props.companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="w-full mt-4">
        {items.length === 0 ? (
          <div className="text-center p-4 text-muted text-[14px]">
            {t(
              "invoice:actions.no_items",
              "No items in this invoice. Add an item below.",
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 mt-4">
            {/* Headers */}
            <div
              className="grid grid-cols-12 gap-2 px-2 py-5 text-sm font-medium text-muted bg-[#f9fafb]"
              style={{
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              <div className="col-span-5 pl-1 text-[14px]">
                {t("invoice:pdf.description", "Description")}
              </div>
              <div className="col-span-1 text-center text-[14px]">
                {t("invoice:pdf.quantity", "Qty")}
              </div>
              <div className="col-span-1 text-center text-[14px]">
                {t("common:unit", "Unit")}
              </div>
              <div className="col-span-2 text-center text-[14px]">
                {t("invoice:pdf.price", "Price")}
              </div>
              <div className="col-span-1 text-center text-[14px]">
                {t("common:vat", "VAT")}
              </div>
              <div className="col-span-2 text-right pr-2 text-[14px]">
                {t("invoice:pdf.total", "Total")}
              </div>
            </div>

            {/* Item rows */}
            {items.map((item, index) => {
              // Calculate total for this item
              const quantity =
                typeof item.quantity === "string" ? 0 : item.quantity;
              const price = typeof item.price === "string" ? 0 : item.price;
              const vat = typeof item.vat === "string" ? 0 : item.vat || 0;
              const discountInPercent =
                typeof item.discountInPercent === "string"
                  ? 0
                  : item.discountInPercent || 0;

              const basePrice = quantity * price;
              const discount = discountInPercent
                ? (basePrice * discountInPercent) / 100
                : 0;
              const priceAfterDiscount = basePrice - discount;
              const vatAmount = vat ? (priceAfterDiscount * vat) / 100 : 0;
              const total = priceAfterDiscount + vatAmount;

              return (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-2 items-center"
                  style={{ marginTop: "16px" }}
                >
                  <div className="col-span-5">
                    {linkedIds.length > 0 && (
                      <Select
                        aria-label={t("invoice:consolidated.source_order")}
                        disabled={Boolean(item.sourceLineId)}
                        value={item.sourceOrderId || ""}
                        onChange={(event) =>
                          handleItemChange(
                            index,
                            "sourceOrderId",
                            event.target.value,
                          )
                        }
                        sizing="sm"
                        className="mb-2"
                      >
                        <option value="" disabled>
                          {t("invoice:consolidated.source_order")}
                        </option>
                        {sourceOrders.map((source) => (
                          <option key={source.orderId} value={source.orderId}>
                            #{source.orderNumber}
                          </option>
                        ))}
                      </Select>
                    )}
                    <TextInput
                      id={`item-${index}-description`}
                      type="text"
                      value={item.description}
                      onChange={(e) =>
                        handleItemChange(index, "description", e.target.value)
                      }
                      placeholder={t(
                        "invoice:actions.item_description_placeholder",
                        "Item description",
                      )}
                      className="w-full text-[14px]"
                      sizing="sm"
                      style={{ fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-span-1">
                    {item.displayQuantity ? (
                      <div className="w-full text-center text-[14px] py-2 text-gray-700">
                        {item.displayQuantity}
                      </div>
                    ) : (
                      <TextInput
                        id={`item-${index}-quantity`}
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, "quantity", e.target.value)
                        }
                        className="w-full text-center text-[14px] no-spinner"
                        min="0"
                        step="1"
                        sizing="sm"
                        style={{
                          fontSize: "14px",
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                          appearance: "textfield",
                        }}
                      />
                    )}
                  </div>
                  <div className="col-span-1">
                    <Select
                      id={`item-${index}-unit`}
                      value={item.unit || "stk"}
                      onChange={(e) =>
                        handleItemChange(index, "unit", e.target.value)
                      }
                      className="w-full text-center text-[14px]"
                      sizing="sm"
                      style={{ fontSize: "14px" }}
                    >
                      {unitOptions.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <TextInput
                      id={`item-${index}-price`}
                      type="number"
                      value={item.price}
                      onChange={(e) =>
                        handleItemChange(index, "price", e.target.value)
                      }
                      className="w-full text-right text-[14px] no-spinner"
                      min="0"
                      step="0.01"
                      sizing="sm"
                      style={{
                        fontSize: "14px",
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                        appearance: "textfield",
                      }}
                    />
                  </div>
                  <div className="col-span-1">
                    <Select
                      id={`item-${index}-vat`}
                      value={item.vat === undefined ? "" : item.vat}
                      onChange={(e) =>
                        handleItemChange(index, "vat", e.target.value)
                      }
                      className="w-full text-right text-[14px] no-spinner"
                      sizing="sm"
                      style={{ fontSize: "14px" }}
                    >
                      {vatOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-1 text-right font-medium text-secondary flex items-center justify-end text-[14px]">
                    {total.toFixed(2)}
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <button
                      className="p-1 text-red-600 rounded-md hover:bg-red-100 flex items-center justify-center"
                      onClick={() => handleRemoveItem(index)}
                      title={t("common:delete", "Delete")}
                      type="button"
                    >
                      <HiOutlineTrash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add new item button */}
        <div className="mt-6 mb-3">
          <Button
            color="light"
            onClick={handleAddNewItem}
            className="flex items-center gap-1 text-[14px]"
            style={{ fontSize: "14px" }}
          >
            <HiPlus className="h-4 w-4" />
            {t("invoice:actions.add_item", "Ny fakturalinje")}
          </Button>
        </div>
      </div>

      {/* Summary section */}
      <div className="w-full mt-2">
        <div className="flex justify-end">
          <div className="w-64">
            <div className="flex justify-between text-[14px] text-muted my-1">
              <span>{t("invoice:actions.subtotal", "Subtotal")}:</span>
              <span>
                {items
                  .reduce((sum, item) => {
                    const quantity =
                      typeof item.quantity === "string" ? 0 : item.quantity;
                    const price =
                      typeof item.price === "string" ? 0 : item.price;
                    const discountInPercent =
                      typeof item.discountInPercent === "string"
                        ? 0
                        : item.discountInPercent || 0;

                    const basePrice = quantity * price;
                    const discount = discountInPercent
                      ? (basePrice * discountInPercent) / 100
                      : 0;
                    return sum + basePrice - discount;
                  }, 0)
                  .toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-[14px] text-muted my-1">
              <span>{t("common:vat", "VAT")}:</span>
              <span>
                {items
                  .reduce((sum, item) => {
                    const quantity =
                      typeof item.quantity === "string" ? 0 : item.quantity;
                    const price =
                      typeof item.price === "string" ? 0 : item.price;
                    const vat =
                      typeof item.vat === "string" ? 0 : item.vat || 0;
                    const discountInPercent =
                      typeof item.discountInPercent === "string"
                        ? 0
                        : item.discountInPercent || 0;

                    const basePrice = quantity * price;
                    const discount = discountInPercent
                      ? (basePrice * discountInPercent) / 100
                      : 0;
                    const priceAfterDiscount = basePrice - discount;
                    return sum + (vat ? (priceAfterDiscount * vat) / 100 : 0);
                  }, 0)
                  .toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between font-medium text-secondary mt-2 pt-2 border-t text-[14px]">
              <span>{t("invoice:pdf.total", "Total")}:</span>
              <span>
                {items
                  .reduce((sum, item) => {
                    const quantity =
                      typeof item.quantity === "string" ? 0 : item.quantity;
                    const price =
                      typeof item.price === "string" ? 0 : item.price;
                    const vat =
                      typeof item.vat === "string" ? 0 : item.vat || 0;
                    const discountInPercent =
                      typeof item.discountInPercent === "string"
                        ? 0
                        : item.discountInPercent || 0;

                    const basePrice = quantity * price;
                    const discount = discountInPercent
                      ? (basePrice * discountInPercent) / 100
                      : 0;
                    const priceAfterDiscount = basePrice - discount;
                    const vatAmount = vat
                      ? (priceAfterDiscount * vat) / 100
                      : 0;
                    return sum + priceAfterDiscount + vatAmount;
                  }, 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {invalidSources && (
        <p role="alert" className="text-sm text-red-600">
          {t("invoice:consolidated.keep_source_lines")}
        </p>
      )}
      <div className="flex gap-4 mt-4 w-full justify-end">
        <Button
          color="gray"
          onClick={() => props.toggleModal()}
          className="text-[14px]"
        >
          {t("common:cancel")}
        </Button>
        <Button
          onClick={() => props.confirmButton(items, companyId)}
          color="primary"
          disabled={
            props.isLoading ||
            !companyId ||
            selectedCompanyIsDeleted ||
            invalidSources
          }
          className="text-[14px]"
        >
          {t("common:save")}
          {props.isLoading && (
            <div className="ml-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};
